import { useState } from 'react';
import { Users, Calendar, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDashboardStats } from '../../queries/dashboard';
import { AnalyticsRange, useAnalyticsOverview } from '../../queries/analytics';
import { RealtimeActivityChart } from './RealtimeActivityChart';
import { ActivityTrendTab, FunnelTab, OverviewTab, PageClicksTab } from './DashboardCharts';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard } from '../../components/ui/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';

const QUICK_ACTIONS = [
  { to: '/meetings', label: 'Meetings' },
  { to: '/users', label: 'Users' },
  { to: '/subscriptions', label: 'Subscriptions' },
  { to: '/marketing', label: 'Marketing' },
];

const RANGES: { value: AnalyticsRange; label: string }[] = [
  { value: '1d', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const TABS = ['Overview', 'Activity Trends', 'Page Clicks', 'Registration Funnel'] as const;
type Tab = (typeof TABS)[number];

export function DashboardPage() {
  const { data: stats, isLoading } = useDashboardStats();
  const [range, setRange] = useState<AnalyticsRange>('7d');
  const [tab, setTab] = useState<Tab>('Overview');
  const { data: overview } = useAnalyticsOverview(range);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="User analytics and system overview."
        actions={
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as AnalyticsRange)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        }
      />

      {/* System snapshot tiles. */}
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Users} label="Total Users" value={stats?.totalUsers} />
          <StatCard icon={Calendar} label="Meetings Today" value={stats?.meetingsToday} />
          <StatCard icon={CreditCard} label="Active Subscriptions" value={stats?.activeSubscriptions} />
        </div>
      )}

      {/* Live per-user activity graph. */}
      <RealtimeActivityChart />

      {/* Analytics overview tiles for the selected range. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Users (tracked)" value={overview?.totalUsers} />
        <StatCard label="Active Users" value={overview?.activeUsers} />
        <StatCard label="New Registrations" value={overview?.newRegistrations} />
        <StatCard label="Login Success Rate" value={overview ? `${overview.loginSuccessRate.toFixed(1)}%` : undefined} />
      </div>

      {/* Tabbed detail. */}
      <Card>
        <div className="flex gap-1 overflow-x-auto border-b border-border p-2">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                tab === t ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {t}
            </button>
          ))}
        </div>
        <CardContent>
          {tab === 'Overview' && <OverviewTab range={range} />}
          {tab === 'Activity Trends' && <ActivityTrendTab range={range} />}
          {tab === 'Page Clicks' && <PageClicksTab range={range} />}
          {tab === 'Registration Funnel' && <FunnelTab range={range} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 pt-0">
          {QUICK_ACTIONS.map((action) => (
            <Button key={action.to} asChild variant="outline" size="sm">
              <Link to={action.to}>{action.label}</Link>
            </Button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
